import React, { useContext, useEffect } from 'react';

import UploadImage from '@/components/UploadImage';
import { PageOptionContext, PageOptionDispatchContext } from '@/components/dashboard/context';
import { DEFAULT_META_DESCRIPTION, LOGO_TWITTER } from '@/constants';
import sharedStyles from '@/styles/Shared.module.css';
import { Link } from '@styled-icons/feather';
import { Text } from '@typedream/ui';
import classNames from 'classnames';
import Image from 'next/legacy/image';
import { PagesOptionTwitterProps } from './types';

// SEO Settings:
// title = twitter.title || metatags.title || space.name/page.name || LOGO_NAME
// description = twitter.description || metatags.description ||  DEFAULT_META_DESCRIPTION
// username = twitter.username || LOGO_TWITTER
// image = twitter.image

export function PagesOptionTwitter({
  columns,
  disabled = false,
  onRemoveCard,
}: PagesOptionTwitterProps) {
  const { page, meta, twitter, twitterImage } = useContext(PageOptionContext);
  const { setTwitter, setTwitterImage } = useContext(PageOptionDispatchContext);
  const imagePreview = React.useRef(null);

  useEffect(() => {
    if (twitterImage) {
      const reader = new FileReader();
      const { current } = imagePreview;
      current.file = twitterImage;
      reader.onload = (e) => {
        current.src = e.target.result;
      };
      reader.readAsDataURL(twitterImage);
    }
  }, [twitterImage]);

  return (
    <div className={classNames({ columns })}>
      <div className={classNames({ column: columns })}>
        <Text as="p" size="md" className="mb-2">
          Twitter Image
        </Text>
        <UploadImage
          setfile={setTwitterImage}
          file={twitterImage}
          containerWidth={100}
          containerHeight={100}
          width={100}
          height={100}
          borderRadius={0}
          className="object-cover mb-4"
          placeholderImage={twitter.imageURL || '/images/image-placeholder.png'}
          disabled={disabled}
          onRemoveCard={onRemoveCard}
        />
        <Text as="p" size="md" className="mb-2">
          Twitter Title
        </Text>
        <input
          className={classNames(sharedStyles.inputMedium, 'mb-4')}
          type="text"
          value={twitter.title || ''}
          placeholder={meta.title || page.name}
          onChange={(e) => setTwitter((curr) => ({ ...curr, title: e.target.value }))}
          disabled={disabled}
        />

        <Text as="p" size="md" className="mb-2">
          Twitter Description
        </Text>
        <textarea
          className={classNames(sharedStyles.inputMedium, '!h-[100px] mb-4')}
          value={twitter.description || ''}
          placeholder={meta.description || DEFAULT_META_DESCRIPTION}
          onChange={(e) => setTwitter((curr) => ({ ...curr, description: e.target.value }))}
          disabled={disabled}
        />
        <Text as="p" size="md" className="mb-2">
          Twitter Username
        </Text>
        <input
          className={classNames(sharedStyles.inputMedium, 'mb-4')}
          type="text"
          value={twitter.username || ''}
          placeholder={LOGO_TWITTER}
          onChange={(e) => setTwitter((curr) => ({ ...curr, username: e.target.value }))}
          disabled={disabled}
        />
      </div>

      <div className={classNames({ column: columns })}>
        <Text as="p" size="xs" className="mb-2">
          Twitter Preview
        </Text>
        <div
          className="w-full, my-4 mx-0 py-4 px-5 rounded-lg bg-neutral-25"
          style={{ boxShadow: 'var(--shadow-modal)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-[30px] h-[30px] rounded-full mr-2 bg-[#00000033]" />
              <div>
                <Text as="p" size="xs" weight="bold" className="text-neutral-900 !leading-4">
                  Name
                </Text>
                <Text as="p" className="text-[11px] !leading-[14px] text-[#000000b3]">
                  {twitter.username || LOGO_TWITTER}
                </Text>
              </div>
            </div>
            <Image src="/images/twitter.svg" width={23} height={23} />
          </div>
          <div className="bg-[#0000000d] rounded-full my-2 mx-0 h-2.5 w-full" />
          <div className="bg-[#0000000d] rounded-full my-2 mx-0 h-2.5 w-3/4" />
          <div className="mt-4 border border-solid border-[#0000001a] rounded-2xl">
            {(twitterImage || twitter.imageURL) && (
              <img
                src={twitter.imageURL}
                ref={imagePreview}
                className="w-full h-[180px] object-cover rounded-t-2xl mb-0"
                alt="Twitter"
              />
            )}
            <div className="p-3">
              <Text as="p" size="sm" className="text-neutral-900 !leading-6">
                {twitter.title || meta.title || page.name}
              </Text>
              <Text as="p" size="sm" className="text-[#00000066] !leading-6">
                {twitter.description || meta.description || DEFAULT_META_DESCRIPTION}
              </Text>
              <Text as="p" size="sm" className="text-[#00000066] !leading-6">
                <Link size={14} /> website.com
              </Text>
            </div>
          </div>
        </div>
        <Text as="p" size="2xs" weight="semibold" className="!leading-6">
          How do I refresh the card ?
        </Text>
        <Text as="p" size="2xs" variant="secondary" className="!leading-6">
          Twitter crawls your site to update Twitter Cards on a weekly basis. It might take a while
          for changes to be reflected.{' '}
          <a
            href="https://developer.twitter.com/en/docs/twitter-for-websites/cards/guides/troubleshooting-cards#refreshing"
            target="_blank"
            rel="noreferrer"
          >
            Click here
          </a>{' '}
          for more information.
        </Text>
      </div>
    </div>
  );
}
